import React from "react";
import { FormField, FieldType } from "../../types/formTypes";
import FieldHeader from "./FieldHeader";
import SimpleTitleField from "./SimpleTitleField";
import FieldPreview from "./field-preview";
import FieldFooter from "./FieldFooter";
import { handleFieldTypeChange } from "../../utils/fieldUtils";
import { Button } from "../../../../components/ui/button";
import { Copy, Trash2 } from "lucide-react";
import { TooltipWrapper } from "../../../../components/ui/tooltip-wrapper";

interface FormFieldEditorProps {
	field: FormField;
	onUpdateField: (updates: Partial<FormField>) => void;
	onDeleteField: () => void;
	onDuplicateField: () => void;
}

const FormFieldEditor: React.FC<FormFieldEditorProps> = ({
	field,
	onUpdateField,
	onDeleteField,
	onDuplicateField,
}) => {
	const handleUpdateOptions = (newOptions: string[]) => {
		onUpdateField({ options: newOptions });
	};

	const handleTypeChange = (newType: FieldType) => {
		const updatedField = handleFieldTypeChange(field, newType);
		// Update all changed properties
		onUpdateField(updatedField);
	};

	// Special handling for title field
	if (field.type === "title") {
		return (
			<div>
				<SimpleTitleField
					label={field.label}
					description={field.description}
					onChange={(label) => onUpdateField({ label })}
					onDescriptionChange={(description) =>
						onUpdateField({ description })
					}
				/>
				<div className="flex items-center gap-2 pt-2">
					<TooltipWrapper title="Feld duplizieren" duration={150}>
						<Button
							variant="ghost"
							size="icon"
							onClick={onDuplicateField}
							className="h-8 w-8"
							aria-label="Feld duplizieren"
							data-testid="duplicate-field-button"
						>
							<Copy className="h-4 w-4" />
						</Button>
					</TooltipWrapper>

					<TooltipWrapper title="Feld löschen" duration={150}>
						<Button
							variant="ghost"
							size="icon"
							onClick={onDeleteField}
							className="h-8 w-8"
							aria-label="Feld löschen"
							data-testid="delete-field-button"
						>
							<Trash2 className="h-4 w-4" />
						</Button>
					</TooltipWrapper>
				</div>
			</div>
		);
	}

	// Description type no longer supported
	if (field.type === "description") {
		return (
			<div className="flex flex-col gap-4">
				<div>Dieser Feldtyp ist nicht mehr verfügbar.</div>
				<Button onClick={onDeleteField}>Feld löschen</Button>
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-4">
			<FieldHeader
				label={field.label}
				onLabelChange={(label) => onUpdateField({ label })}
				fieldType={field.type}
				onFieldTypeChange={handleTypeChange}
			/>
			<div className="h-px bg-gray-200" />
			<div>
				<FieldPreview
					field={field}
					onUpdateOptions={handleUpdateOptions}
					onUpdateField={onUpdateField}
				/>
			</div>
			<div className="h-px bg-gray-200" />
			<FieldFooter
				fieldType={field.type}
				required={field.required}
				description={field.description}
				placeholder={field.placeholder}
				multi={field.multi}
				onUpdate={onUpdateField}
				onDuplicate={onDuplicateField}
				onDelete={onDeleteField}
			/>
		</div>
	);
};

export default FormFieldEditor;
